<script lang="ts">
  import type { Attribute } from '$domain/attributes.ts';
  import {
    cellFor,
    centreOf,
    OVERVIEW_RESOLUTION,
    regionOf,
    REPORT_RESOLUTION,
  } from '$domain/h3.ts';
  import type { UserSettings } from '$domain/settings.ts';
  import { DEFAULT_SETTINGS } from '$domain/settings.ts';
  import { isZone, previewOf } from '$domain/zones.ts';
  import { untrack } from 'svelte';
  import { alerts } from './lib/alerts.svelte.ts';
  import { type ClientConfig, getClientConfig, OfflineError, request } from './lib/api.ts';
  import AdminPanel from './lib/AdminPanel.svelte';
  import Avatar from './lib/Avatar.svelte';
  import CellSheet from './lib/CellSheet.svelte';
  import { deck } from './lib/deck.svelte.ts';
  import DensityMap, { type MapLayer } from './lib/DensityMap.svelte';
  import { GDI_STOPS } from './lib/gdi-scale.ts';
  import HotspotList from './lib/HotspotList.svelte';
  import Wordmark from './lib/Wordmark.svelte';
  import Leaderboard from './lib/Leaderboard.svelte';
  import PairScreen from './lib/PairScreen.svelte';
  import Profile from './lib/Profile.svelte';
  import FlagInbox from './lib/FlagInbox.svelte';
  import { flags } from './lib/flags.svelte.ts';
  import PendingScreen from './lib/PendingScreen.svelte';
  import ReportSheet from './lib/ReportSheet.svelte';
  import { claimFromLaunchUrl, restoreDeviceToken } from './lib/pairing.ts';
  import { exitApp, hasBackgroundAlerts, isNative, onBackButton } from './lib/platform.ts';
  import { outbox } from './lib/outbox.svelte.ts';
  import { reactions } from './lib/reactions.svelte.ts';
  import { askForCompass } from './lib/orientation.ts';
  import { registerServiceWorker } from './lib/push.ts';
  import { session } from './lib/session.svelte.ts';
  import SettingsSheet from './lib/SettingsSheet.svelte';
  import SightingSheet from './lib/SightingSheet.svelte';
  import SyncChip from './lib/SyncChip.svelte';
  import Weather from './lib/Weather.svelte';
  import SwipeDeck from './lib/SwipeDeck.svelte';
  import SignIn from './lib/SignIn.svelte';
  import { describeAge, readCache } from './lib/store.ts';
  import type { CellCollection, Sighting } from './lib/types.ts';
  import Viewfinder from './lib/Viewfinder.svelte';
  import Wrapped from './lib/Wrapped.svelte';
  import ZoneChat from './lib/ZoneChat.svelte';
  import { zone } from './lib/zone.svelte.ts';

  let config = $state<ClientConfig | undefined>();
  let attributes = $state<Attribute[]>([]);
  let sightings = $state<Sighting[]>([]);
  let cells = $state<CellCollection | undefined>();
  let offline = $state(false);

  let layer = $state<MapLayer>('hex');
  /** `alter:low`, `statur:high`, … — brief §14's map filter over the scale attributes. */
  let facet = $state<string | undefined>();
  let view = $state<{ bbox: [number, number, number, number]; zoom: number } | undefined>();
  let showHotspots = $state(false);
  // The "press again to leave" state. Set by the first back press with nothing left to close.
  let leaving = $state(false);
  let showSettings = $state(false);
  /** Umschau, the camera view (amendment 18): the whole screen while it is open. */
  let showViewfinder = $state(false);
  /** Hotspotter Wrapped (amendment 26), opened from the profile and over it. */
  let showWrapped = $state(false);
  /** One panel at a time: the map is the app, and two overlays over it is a mess. */
  let panel = $state<'none' | 'leaderboard' | 'weather' | 'profile' | 'admin' | 'flags' | 'chat'>(
    'none',
  );
  let droughtDays = $state<number | null | undefined>();
  /** How old what is on the map is, when it came from the cache rather than the network. */
  let cacheAge = $state<string | undefined>();
  /** `undefined` means live; a day string means the time-lapse is driving the map. */
  let replayDay = $state<string | undefined>();
  let frames = $state<{ day: string; cells: { cell: string; gdi: number; boundary: [number, number][] }[] }[]>([]);
  let settings = $state<UserSettings>(DEFAULT_SETTINGS);

  let sheet = $state<
    | {
        position: { lat: number; lon: number; accuracy: number };
        source: 'map' | 'quick' | 'passby';
      }
    | undefined
  >();
  let selectedId = $state<string | undefined>();
  let selectedCell = $state<string | undefined>();
  let locating = $state(false);
  // Where the map should move to. Set once at start-up if the browser already allows it, and
  // never again — after that the map is wherever the person dragged it to.
  let focus = $state<{ lat: number; lon: number; zoom?: number } | undefined>();
  let locationError = $state('');
  /** Remembered so the quick report is genuinely two taps (brief §14). */
  let lastRatings = $state<Record<string, number>>({});

  /**
   * The one route this app has besides the map.
   *
   * Read once at start-up rather than kept in sync with history: `/pair` is opened in a
   * Custom Tab, used once and closed, so there is nothing to navigate back to and a router
   * would be a dependency bought for a single screen.
   */
  const pairCode =
    typeof location === 'undefined' || location.pathname !== '/pair'
      ? undefined
      : (new URLSearchParams(location.search).get('code') ?? '');

  const selected = $derived(sightings.find((entry) => entry.id === selectedId));

  /**
   * What the map draws: the live scores, or a time-lapse frame standing in for them.
   *
   * Substituting rather than adding a layer keeps one thing on screen at a time — a replay
   * drawn over today would be two overlapping truths and no way to tell them apart.
   */
  const shownCells = $derived.by<CellCollection | undefined>(() => {
    if (replayDay === undefined) return cells;
    const frame = frames.find((entry) => entry.day === replayDay);
    if (frame === undefined) return { type: 'FeatureCollection', resolution: 9, features: [] };
    return {
      type: 'FeatureCollection',
      resolution: 9,
      features: frame.cells.map((entry) => ({
        type: 'Feature' as const,
        id: entry.cell,
        geometry: { type: 'Polygon' as const, coordinates: [entry.boundary] },
        properties: {
          cell: entry.cell,
          gdi: entry.gdi,
          vibe: null,
          vibeVotes: 0,
          count: 0,
          topAttribute: null,
          facets: {},
          lastAt: null,
          computedAt: frame.day,
        },
      })),
    };
  });
  const scaleAttributes = $derived(attributes.filter((entry) => entry.kind === 'scale'));

  // Brief §14: the overview resolution below zoom 12. A res-9 hexagon at zoom 10 is four
  // pixels across, so the detail is not merely wasted, it is a smear.
  const resolution = $derived(
    (view?.zoom ?? 13) < 12 ? OVERVIEW_RESOLUTION : REPORT_RESOLUTION,
  );

  $effect(() => {
    void (async () => {
      // Before anything else asks the server who we are: inside the APK the session is a
      // bearer token from pairing, and a `/api/auth/me` sent without it answers "nobody".
      //
      // The launch URL is checked when there is no stored token, and only then. It is the
      // cold-start half of the App Link: Android reclaiming the app while the pairing Custom
      // Tab is in front of it is ordinary, and the code then survives nowhere but in the URL
      // this app was started with.
      if (!(await restoreDeviceToken())) await claimFromLaunchUrl();
      try {
        config = await getClientConfig();
        offline = false;
      } catch (cause) {
        offline = cause instanceof OfflineError;
      }
      await session.refresh();
    })();
  });

  $effect(() => {
    if (session.user?.status !== 'active') return;
    void (async () => {
      attributes = (await request<{ attributes: Attribute[] }>('/api/attributes')).attributes;
      settings = (await request<{ settings: UserSettings }>('/api/me/settings')).settings;

      // The service worker carries push, the notification click handler, the offline shell,
      // the tile cache — and the update check that pulls a new build in without anybody
      // clearing their site data. Registering it before the settings are known would be one
      // request earlier and no use, since nothing can be delivered until somebody opts in.
      // Inside the APK there is no service worker and no Web Push: notifications there are
      // local and go through the Capacitor plugin instead (brief §15, §16).
      if (!isNative()) await registerServiceWorker();
      alerts.start(settings.alerts);

      // Not awaited: the map is already on screen and usable, and a slow fix must not hold up
      // the outbox behind it.
      void focusOnMeIfAllowed();

      // The queue is read from IndexedDB and flushed. Started after the session is known,
      // because a flush before sign-in gets a 401 and would raise "please sign in" on a
      // session that is merely still loading.
      await outbox.start();

      // Wabenflaggen (amendment 13). A push about a request points at `/?flags`, which opens
      // the inbox; the parameter is dropped afterwards so a reload does not open it again.
      await flags.refresh();
      if (new URLSearchParams(location.search).has('flags')) {
        panel = 'flags';
        const url = new URL(location.href);
        url.searchParams.delete('flags');
        history.replaceState(history.state, '', url);
      }

      // Zonenchat (amendment 16). A push about a message points at `/?zone=<district>`, which
      // opens that district's chat even if this device has moved on since; the parameter goes
      // the way `flags` does.
      zone.start();
      const pushed = new URLSearchParams(location.search).get('zone');
      if (pushed !== null) {
        if (isZone(pushed)) {
          zone.show(pushed);
          panel = 'chat';
        }
        const url = new URL(location.href);
        url.searchParams.delete('zone');
        history.replaceState(history.state, '', url);
      }
    })();

    // Coming back to the app is when a request is most likely to be waiting — it is what the
    // push was about. The counter should already say so.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flags.refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alerts.stop();
      zone.stop();
      document.removeEventListener('visibilitychange', onVisible);
    };
  });

  // The chat panel and the chat's polling are one thing seen from two sides: open, it looks for
  // new messages every few seconds and counts them read; closed, every half minute at most.
  $effect(() => {
    const chat = panel === 'chat';
    untrack(() => {
      if (chat) zone.show();
      else if (zone.open) zone.hide();
    });
  });

  /**
   * Refetch whenever the view, the layer or the filter changes.
   *
   * Reading the reactive values at the top and *then* going async matters: an `await` before
   * the last read would leave the effect subscribed to whatever it happened to touch first,
   * and panning would silently stop refreshing.
   */
  $effect(() => {
    const current = view;
    const wanted = layer;
    const filter = facet;
    const res = resolution;
    if (session.user?.status !== 'active' || current === undefined) return;
    void load(current.bbox, wanted, filter, res);
  });

  // Waben-Reaktionen (amendment 24): asked about around the middle of what the map shows, and
  // only while somebody is signed in and admitted to see them.
  $effect(() => {
    const current = view;
    if (current === undefined) return;
    const [west, south, east, north] = current.bbox;
    reactions.around((south + north) / 2, (west + east) / 2);
  });

  $effect(() => {
    if (session.user?.status !== 'active') return;
    return reactions.start();
  });

  let inFlight = 0;

  async function load(
    bbox: [number, number, number, number],
    wanted: MapLayer,
    filter: string | undefined,
    res: number,
  ) {
    const box = bbox.map((value) => value.toFixed(4)).join(',');
    // A pan can outrun its own response. The counter drops an answer that arrives after a
    // newer request went out, which is otherwise a map that flickers back to where it was.
    inFlight += 1;
    const ticket = inFlight;
    try {
      const query = new URLSearchParams({ bbox: box, res: String(res) });
      if (filter !== undefined) query.set('facet', filter);
      const collection = await request<CellCollection>(`/api/cells?${query}`);

      // The individual reports are only fetched for the layer that shows them: they are the
      // biggest response the app makes and nothing else on screen uses them.
      const points =
        wanted === 'dots'
          ? (
              await request<{ sightings: Sighting[] }>(
                `/api/sightings?bbox=${box}&days=28&limit=500`,
              )
            ).sightings
          : sightings;

      if (ticket !== inFlight) return;
      cells = collection;
      sightings = points;
      offline = false;
      cacheAge = undefined;
      // Keyed by what was asked for, so panning back to a box you have already seen finds it
      // in the cache rather than on the network (brief §14a.2).
      void readCache.set(`cells:${box}:${res}:${filter ?? ''}`, collection);
    } catch (cause) {
      // Being offline is a state, never an error dialogue (brief §14a.1). What is on the map
      // stays on the map, and if there is nothing on it yet the cache fills it — with its age
      // shown quietly rather than as a warning.
      offline = cause instanceof OfflineError;
      if (!offline) return;
      const cached = await readCache.get<CellCollection>(`cells:${box}:${res}:${filter ?? ''}`);
      if (cached === undefined || ticket !== inFlight) return;
      cells = cached.value;
      cacheAge = `Stand: ${describeAge(cached.fetchedAt)}`;
    }
  }

  function reload() {
    if (view !== undefined) void load(view.bbox, layer, facet, resolution);
  }

  /** The seven-day slider (brief §17.2). Loaded on demand — nobody opens it every session. */
  async function loadTimelapse() {
    if (view === undefined) return;
    const box = view.bbox.map((value) => value.toFixed(4)).join(',');
    try {
      frames = (
        await request<{ frames: typeof frames }>(`/api/timelapse?bbox=${box}&days=7`)
      ).frames;
      replayDay = frames[0]?.day;
    } catch {
      // The slider simply does not appear. Nothing here is worth a dialogue.
      frames = [];
    }
  }

  /**
   * The drought line, asked for the res-5 region the device is in.
   *
   * It waits for the alert loop to have a position, because that is where the rounding to a
   * region happens — asking earlier would mean a second geolocation call for a chip.
   */
  $effect(() => {
    const position = alerts.position;
    if (position === undefined || session.user?.status !== 'active') return;
    const region = regionOf(cellFor(position.lat, position.lon));
    void (async () => {
      try {
        droughtDays = (await request<{ days: number | null }>(`/api/drought?cell5=${region}`)).days;
      } catch {
        droughtDays = undefined;
      }
    })();
  });

  function toggleFacet(key: string, direction: 'low' | 'high') {
    const value = `${key}:${direction}`;
    facet = facet === value ? undefined : value;
  }

  /**
   * The Waze-style report button: take a fix, then open the sheet.
   *
   * `enableHighAccuracy` matters more than the timeout — the server refuses anything worse
   * than 100 m, so a coarse network fix would be collected and then rejected, which reads as
   * a broken button.
   */
  /**
   * Android's back gesture (item 4 of the first round of app feedback).
   *
   * Registering a listener replaces Capacitor's default entirely, so this owns the whole
   * behaviour. Back closes the topmost thing that is open — which is what it means everywhere
   * else on the platform — and only when there is nothing left does it offer to leave.
   *
   * Two presses rather than one, because a single press that quits is the reason people lose
   * a half-written report to a thumb resting on the wrong edge. The queue would survive it;
   * the note in the sheet would not.
   */
  function closeTopmost(): boolean {
    // Order matters: it is the reverse of the order things stack on screen, so back peels
    // them off the way they were put on.
    if (showWrapped) return ((showWrapped = false), true);
    if (showViewfinder) return ((showViewfinder = false), true);
    if (sheet !== undefined) return ((sheet = undefined), true);
    if (selectedId !== undefined) return ((selectedId = undefined), true);
    if (selectedCell !== undefined) return ((selectedCell = undefined), true);
    if (showSettings) return ((showSettings = false), true);
    if (deck.open) return ((deck.open = false), true);
    if (panel !== 'none') return ((panel = 'none'), true);
    if (showHotspots) return ((showHotspots = false), true);
    if (frames.length > 0) return ((frames = []), (replayDay = undefined), true);
    if (alerts.current !== undefined) return (alerts.dismiss(), true);
    return false;
  }

  let leavingTimer: ReturnType<typeof setTimeout> | undefined;

  function handleBack() {
    if (closeTopmost()) {
      leaving = false;
      return;
    }
    if (leaving) {
      void exitApp();
      return;
    }
    leaving = true;
    clearTimeout(leavingTimer);
    // Long enough to read the hint, short enough that a back press a minute later is not
    // silently the second one.
    leavingTimer = setTimeout(() => (leaving = false), 2500);
  }

  $effect(() => onBackButton(handleBack));

  /**
   * Open where you are, but only if that costs nobody a permission dialogue.
   *
   * The map starts on Stuttgart Marktplatz, which is right for a demonstration and wrong for
   * everybody standing somewhere else — a report made at your own position lands outside the
   * opening view and reads as data that went missing.
   *
   * The rule is `permissions.query`, and it is the whole point: **`granted` only**. On
   * `prompt` this does nothing, because a location dialogue thrown at somebody who has just
   * opened a map is exactly the interruption that gets an app denied for good; the report
   * button asks at the moment the answer is obviously needed. Where `permissions` cannot
   * answer for geolocation — older Safari — asking would be the only way to find out, so this
   * stays put.
   */
  async function focusOnMeIfAllowed() {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return;
    try {
      const status = await navigator.permissions?.query({ name: 'geolocation' });
      if (status?.state !== 'granted') return;
    } catch {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        focus = { lat: fix.coords.latitude, lon: fix.coords.longitude, zoom: 14 };
      },
      () => undefined,
      // No high accuracy and a generous `maximumAge`: this only has to pick the right town,
      // and a cached fix does that without waking the GPS on somebody's battery.
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300_000 },
    );
  }

  function reportHere() {
    locating = true;
    locationError = '';
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        locating = false;
        sheet = {
          position: {
            lat: fix.coords.latitude,
            lon: fix.coords.longitude,
            accuracy: fix.coords.accuracy,
          },
          source: 'quick',
        };
      },
      (cause) => {
        locating = false;
        locationError =
          cause.code === cause.PERMISSION_DENIED
            ? 'Ohne Standortfreigabe geht das Melden nicht.'
            : 'Position lässt sich gerade nicht bestimmen.';
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 10_000 },
    );
  }

  const LAYERS: { id: MapLayer; label: string }[] = [
    { id: 'heat', label: 'Heatmap' },
    { id: 'hex', label: 'Waben' },
    { id: 'dots', label: 'Meldungen' },
  ];
</script>

<main>
  <!-- An Android toast, in the place Android puts them: above the navigation bar, centred,
       and outside every branch below — because back can be pressed on the sign-in screen too,
       and the first version of this lived in the map's banner lane where that state has no
       DOM at all. -->
  {#if leaving}
    <p class="toast" role="status">Nochmal zurück zum Beenden</p>
  {/if}

  {#if pairCode !== undefined}
    <PairScreen code={pairCode} requireInvite={config?.auth.requireInvite ?? true} />
  {:else if session.loading}
    <div class="centre"><p>Einen Moment …</p></div>
  {:else if session.user === null}
    <SignIn
      requireInvite={config?.auth.requireInvite ?? true}
      apk={config?.android?.apk ?? null}
      wearApk={config?.android?.wearApk ?? null}
    />
  {:else if session.user.status !== 'active'}
    <PendingScreen user={session.user} />
  {:else}
    {#if config !== undefined}
      <DensityMap
        styleUrlLight={config.map.styleUrlLight}
        styleUrlDark={config.map.styleUrlDark}
        attribution={config.map.attribution}
        {sightings}
        cells={shownCells}
        {layer}
        onpick={(position) => (sheet = { position, source: 'map' })}
        onselect={(id) => (selectedId = id)}
        oncell={(cell) => (selectedCell = cell)}
        onbounds={(bbox, zoom) => (view = { bbox, zoom })}
        {focus}
        reactions={reactions.rising}
      />
    {/if}

    <div class="topleft">
      <header>
        <h1><Wordmark height="2.9rem" /></h1>
        <span class="who"
          >@{session.user.username}{session.user.role === 'admin' ? ' · Admin' : ''}</span
        >
      </header>

      <nav class="layers" aria-label="Kartenebene">
        {#each LAYERS as entry (entry.id)}
          <button aria-pressed={layer === entry.id} onclick={() => (layer = entry.id)}>
            {entry.label}
          </button>
        {/each}
        <button aria-pressed={showHotspots} onclick={() => (showHotspots = !showHotspots)}>
          Hotspots
        </button>
        {#if deck.cards.length > 0}
          <!-- Brief §9a.1: the deck shows up as a counter on the map — "2 Orte zum Bewerten" —
               rather than as something that interrupts what you were doing. -->
          <button class="deck" aria-pressed={deck.open} onclick={() => (deck.open = true)}>
            {deck.cards.length} {deck.cards.length === 1 ? 'Ort' : 'Orte'} zum Bewerten
          </button>
        {/if}
        <button
          aria-pressed={panel === 'leaderboard'}
          onclick={() => (panel = panel === 'leaderboard' ? 'none' : 'leaderboard')}
        >
          🏆 Rangliste
        </button>
        <button
          aria-pressed={panel === 'weather'}
          onclick={() => (panel = panel === 'weather' ? 'none' : 'weather')}
        >
          🌡️ Wetter
        </button>
        <button
          aria-pressed={panel === 'flags'}
          aria-label={flags.waiting > 0 ? `Flaggen, ${flags.waiting} Anfragen warten` : 'Flaggen'}
          onclick={() => (panel = panel === 'flags' ? 'none' : 'flags')}
        >
          🚩{#if flags.waiting > 0}<span class="waiting">{flags.waiting}</span>{/if}
        </button>
        <button
          aria-pressed={panel === 'chat'}
          aria-label={zone.unread > 0 ? `Zonenchat, ${zone.unread} neue Nachrichten` : 'Zonenchat'}
          onclick={() => (panel = panel === 'chat' ? 'none' : 'chat')}
        >
          💬{#if zone.unread > 0}<span class="waiting">{zone.unread > 9 ? '9+' : zone.unread}</span
            >{/if}
        </button>
        <button
          aria-pressed={showViewfinder}
          aria-label="Umschau mit der Kamera"
          onclick={() => {
            // Safari hands out the compass only when asked from inside a tap: this one.
            void askForCompass();
            showViewfinder = true;
          }}
        >
          📷
        </button>
        <button
          class="me"
          aria-pressed={panel === 'profile'}
          aria-label="Profil"
          onclick={() => (panel = panel === 'profile' ? 'none' : 'profile')}
        >
          <Avatar username={session.user.username} emoji={session.user.avatarEmoji} size={16} />
        </button>
        {#if session.user.role === 'admin'}
          <button
            aria-pressed={panel === 'admin'}
            onclick={() => (panel = panel === 'admin' ? 'none' : 'admin')}
          >
            🛡
          </button>
        {/if}
        <button aria-pressed={showSettings} onclick={() => (showSettings = true)}>⚙</button>
      </nav>

      {#if scaleAttributes.length > 0}
        <nav class="facets" aria-label="Filter">
          {#each scaleAttributes as attribute (attribute.key)}
            <button
              aria-pressed={facet === `${attribute.key}:low`}
              onclick={() => toggleFacet(attribute.key, 'low')}
            >
              {attribute.emoji} {attribute.ends?.[0]}
            </button>
            <button
              aria-pressed={facet === `${attribute.key}:high`}
              onclick={() => toggleFacet(attribute.key, 'high')}
            >
              {attribute.emoji} {attribute.ends?.[1]}
            </button>
          {/each}
        </nav>
      {/if}

      <div class="banners">
        {#if alerts.current !== undefined}
          <!-- Brief §14: a banner at the top when you are standing in a hotspot. It says the same
               thing as the notification, for the case where the app is already open and a system
               notification would be redundant or suppressed. -->
          <aside class="alert" role="status">
            <strong>⚠️ Erhöhte Geschicktendichte</strong>
            <span>
              GDI {alerts.current.gdi} · {alerts.current.sightingCount} Meldungen
            </span>
            <button onclick={() => alerts.dismiss()} aria-label="Warnung schließen">×</button>
          </aside>
        {/if}

        <!-- Brief §14a.8: one chip, small, always in the same place. It is the entire offline
             interface — there is no dialogue and no red bar anywhere in this app. -->
        <SyncChip {offline} stale={cacheAge} />
      </div>
    </div>

    {#if layer === 'hex' || layer === 'heat'}
      <ul class="legend" aria-label="Farbskala">
        {#each GDI_STOPS as stop (stop.at)}
          <li><i style={`background: ${stop.colour}`}></i>{stop.at}</li>
        {/each}
      </ul>
    {/if}


    <!-- One column, not three absolutely positioned siblings. Every chip in here wraps
         on a narrow screen, and anything anchored to a fixed offset above it gets
         climbed into the moment it does. -->
    <div class="dock">
      {#if showHotspots}
        <HotspotList
          bbox={view?.bbox}
          onclose={() => (showHotspots = false)}
          onpick={(cell) => {
            selectedCell = cell;
            showHotspots = false;
          }}
        />
      {/if}
      {#if frames.length > 0}
        <div class="replay">
          <input
            type="range"
            min="0"
            max={frames.length}
            step="1"
            value={replayDay === undefined
              ? frames.length
              : frames.findIndex((entry) => entry.day === replayDay)}
            oninput={(event) => {
              const index = Number(event.currentTarget.value);
              replayDay = index >= frames.length ? undefined : frames[index]?.day;
            }}
            aria-label="Zeitraffer"
          />
          <span>{replayDay ?? 'jetzt'}</span>
          <button onclick={() => { frames = []; replayDay = undefined; }} aria-label="Zeitraffer aus">
            ×
          </button>
        </div>
      {:else}
        <button class="replay-open" onclick={loadTimelapse}>⏱ Zeitraffer</button>
      {/if}
      <footer>
        <span class="chip">
          {cells?.features.length ?? 0} Waben · {resolution === OVERVIEW_RESOLUTION
            ? 'Übersicht'
            : 'Detail'}
        </span>
        {#if locationError}<span class="chip warn">{locationError}</span>{/if}
        {#if droughtDays !== undefined && droughtDays !== null && droughtDays >= 2}
          <!-- Brief §17.2: small text, large effect. It is the reason somebody opens the app on
               a quiet Tuesday. -->
          <span class="chip">Seit {droughtDays} Tagen kein Fund in deinem Umkreis.</span>
        {/if}
        {#if !hasBackgroundAlerts() && settings.alerts.enabled}
          <!-- Said once, quietly, where somebody who turned alerts on will see it. A PWA cannot
               run a geofence while it is closed, and there is no web API that changes that. -->
          <span class="chip">Warnungen nur bei geöffneter App</span>
        {/if}
      </footer>
    </div>

    <button class="fab" onclick={reportHere} disabled={locating} aria-label="Fund melden">
      {locating ? '…' : '⚠'}
    </button>

    {#if deck.open}
      <SwipeDeck
        {attributes}
        onclose={() => (deck.open = false)}
        onrate={(cell) => {
          deck.open = false;
          // The up-swipe opens the ordinary report sheet at the cell centre. It creates a real
          // report through POST /api/sightings; a swipe never does (brief §9a.2).
          const centre = centreOf(cell);
          sheet = {
            position: { lat: centre.lat, lon: centre.lon, accuracy: 0 },
            source: 'passby',
          };
        }}
      />
    {/if}

    {#if showSettings}
      <SettingsSheet
        {settings}
        apk={config?.android?.apk ?? null}
        wearApk={config?.android?.wearApk ?? null}
        onclose={() => (showSettings = false)}
        onsaved={(saved) => {
          settings = saved;
          alerts.update(saved.alerts);
        }}
      />
    {/if}


    {#if panel === 'leaderboard'}
      <Leaderboard onclose={() => (panel = 'none')} />
    {/if}

    {#if panel === 'weather'}
      <Weather
        bbox={view?.bbox}
        onclose={() => (panel = 'none')}
        onpick={(cell) => {
          panel = 'none';
          focus = { ...centreOf(cell), zoom: 15 };
          selectedCell = cell;
        }}
      />
    {/if}

    {#if panel === 'profile'}
      <Profile onclose={() => (panel = 'none')} onwrapped={() => (showWrapped = true)} />
    {/if}

    {#if panel === 'flags'}
      <FlagInbox
        onclose={() => (panel = 'none')}
        onselect={(cell) => {
          panel = 'none';
          selectedCell = cell;
        }}
      />
    {/if}

    {#if panel === 'chat'}
      <ZoneChat onclose={() => (panel = 'none')} />
    {/if}

    <!-- Zonenchat (amendment 16): somebody wrote in your district while the chat was closed, or
         took your invitation to a game (amendment 19). A snackbar rather than a dialogue — it
         says so, and a tap opens the chat. -->
    {#if zone.snack !== undefined && panel !== 'chat'}
      <button class="snack" onclick={() => (panel = 'chat')}>
        {zone.snack.icon} <strong>@{zone.snack.who}</strong>
        {previewOf(zone.snack.text, 60)}
      </button>
    {/if}

    {#if panel === 'admin' && session.user.role === 'admin'}
      <AdminPanel onclose={() => (panel = 'none')} />
    {/if}

    {#if selectedCell !== undefined}
      <CellSheet
        cell={selectedCell}
        {attributes}
        onclose={() => (selectedCell = undefined)}
        onsighting={(id) => {
          selectedCell = undefined;
          selectedId = id;
          // The report sheet reads from `sightings`, which the hex layer does not fetch.
          if (layer !== 'dots') layer = 'dots';
        }}
      />
    {/if}

    {#if selected !== undefined && session.user !== null}
      <SightingSheet
        sighting={selected}
        {attributes}
        viewerId={session.user.id}
        onclose={() => (selectedId = undefined)}
        onvoted={(result) => {
          // A vote that crossed the auto-hide threshold removes the report from everybody
          // else's map, so the sheet has nothing left to show.
          if (result.hiddenNow) selectedId = undefined;
          reload();
        }}
      />
    {/if}

    {#if sheet !== undefined}
      <ReportSheet
        {attributes}
        position={sheet.position}
        source={sheet.source}
        initial={lastRatings}
        onclose={() => (sheet = undefined)}
        onsaved={(ratings) => {
          lastRatings = ratings;
          sheet = undefined;
          reload();
        }}
      />
    {/if}

    {#if showViewfinder}
      <Viewfinder {attributes} onclose={() => (showViewfinder = false)} />
    {/if}

    {#if showWrapped}
      <Wrapped {attributes} onclose={() => (showWrapped = false)} />
    {/if}
  {/if}
</main>

<style>
  main {
    position: relative;
    height: 100%;
    overflow: hidden;
  }

  .centre {
    display: grid;
    place-items: center;
    height: 100%;
    color: var(--muted);
  }

  /*
   * Everything that hangs over the top-left of the map, in one column.
   *
   * It used to be three absolutely positioned siblings at hand-measured offsets — `top: 4rem`
   * for the layer chips, `top: 6.5rem` for the filters — which holds only while each row is
   * exactly one line tall. It is not: the chips wrap, and on a 390 px screen the layer row
   * takes two lines and grows straight through the filters underneath it. A column that
   * flows cannot have that bug at any width.
   */
  .topleft {
    position: absolute;
    top: calc(env(safe-area-inset-top) + 0.75rem);
    left: calc(env(safe-area-inset-left) + 1rem);
    /* Stop short of the zoom and locate buttons MapLibre puts in the opposite corner. */
    right: 4rem;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    /* The map has to stay draggable through the gaps in this column. */
    pointer-events: none;
  }

  .topleft > :not(header) {
    pointer-events: auto;
  }

  header {
    pointer-events: none;
  }

  /* Transient, and centred in the column so a warning does not read as another filter row. */
  .banners {
    align-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    max-width: 100%;
  }

  h1 {
    margin: 0;
    /* The wordmark is a block image; without this the line box adds descender space under it
       and the username below stops sitting where it looks like it should. */
    line-height: 0;
  }

  .who {
    font-size: 0.8125rem;
    color: var(--muted);
    text-shadow: 0 1px 3px var(--bg);
  }

  .layers,
  .facets {
    display: flex;
    gap: 0.35rem;
    flex-wrap: wrap;
    max-width: 100%;
  }

  .layers button,
  .facets button {
    padding: 0.3rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--card);
    color: var(--muted);
    font-size: 0.75rem;
    cursor: pointer;
  }

  .layers .deck {
    border-color: var(--accent);
    color: var(--fg);
  }

  /* The face in the pill, frame and all, without making the pill taller than its neighbours. */
  .layers .me :global(.avatar) {
    margin-block: -3px;
  }

  .layers button[aria-pressed='true'],
  .facets button[aria-pressed='true'] {
    border-color: var(--accent);
    color: var(--fg);
    background: color-mix(in srgb, var(--accent) 18%, var(--card));
  }

  /*
   * The bottom-left column, and the same story as `.topleft`: the footer chips wrap, so the
   * time-lapse control anchored 4rem above the bottom ended up sitting on them.
   */
  .dock {
    position: absolute;
    left: calc(env(safe-area-inset-left) + 1rem);
    /* Above the attribution, which MapLibre parks in this same corner: its own inset, plus
       10px of MapLibre margin, plus the 24px button, plus air. */
    bottom: calc(env(safe-area-inset-bottom) + 2.75rem);
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
    /* Clear of the report button: 3.75rem of circle, 1rem of gutter, 1rem of air. */
    max-width: calc(100% - 6.75rem);
  }

  .replay {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: min(22rem, 100%);
    padding: 0.4rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--card);
    font-size: 0.75rem;
    color: var(--muted);
  }

  .replay input {
    flex: 1;
    accent-color: var(--accent);
  }

  .replay button,
  .replay-open {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 0.75rem;
    cursor: pointer;
  }

  .replay-open {
    padding: 0.3rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--card);
  }

  .legend {
    position: absolute;
    right: 1rem;
    bottom: calc(env(safe-area-inset-bottom) + 5.25rem);
    z-index: 1;
    display: grid;
    gap: 0.15rem;
    margin: 0;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--card);
    list-style: none;
    font-size: 0.6875rem;
    color: var(--muted);
  }

  .legend li {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .legend i {
    display: inline-block;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 0.15rem;
  }

  footer {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    max-width: 100%;
  }

  .chip {
    padding: 0.3rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--card);
    color: var(--muted);
    font-size: 0.75rem;
  }

  .chip.warn {
    border-color: var(--accent);
    color: var(--accent);
  }

  .toast {
    position: absolute;
    left: 50%;
    bottom: calc(env(safe-area-inset-bottom) + 5rem);
    transform: translateX(-50%);
    /* Above every sheet and panel: it is the answer to a press that happened over them. */
    z-index: 10;
    margin: 0;
    padding: 0.55rem 1.1rem;
    border-radius: 999px;
    background: var(--fg);
    color: var(--bg);
    font-size: 0.8125rem;
    white-space: nowrap;
    box-shadow: 0 4px 16px rgb(0 0 0 / 30%);
    pointer-events: none;
  }

  .alert {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    max-width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--accent);
    border-radius: 999px;
    background: var(--card);
    font-size: 0.8125rem;
    box-shadow: 0 4px 16px rgb(0 0 0 / 25%);
  }

  .alert span {
    color: var(--muted);
  }

  .alert button {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
  }

  /* Bottom right, thumb-sized, exactly where Google Maps puts its reporting button. */
  .fab {
    position: absolute;
    right: calc(env(safe-area-inset-right) + 1rem);
    bottom: calc(env(safe-area-inset-bottom) + 1rem);
    z-index: 2;
    width: 3.75rem;
    height: 3.75rem;
    border: 0;
    border-radius: 50%;
    background: var(--accent);
    color: #fff;
    font-size: 1.6rem;
    box-shadow: 0 4px 16px rgb(0 0 0 / 30%);
    cursor: pointer;
  }

  .fab:disabled {
    opacity: 0.6;
  }

  /* The district chat's snackbar: the toast's shape, but something to tap. */
  .snack {
    position: absolute;
    left: 50%;
    bottom: calc(env(safe-area-inset-bottom) + 5rem);
    transform: translateX(-50%);
    z-index: 9;
    max-width: min(26rem, calc(100% - 2rem));
    padding: 0.6rem 1.1rem;
    border: 0;
    border-radius: 999px;
    background: var(--fg);
    color: var(--bg);
    font: inherit;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-shadow: 0 4px 16px rgb(0 0 0 / 30%);
    cursor: pointer;
  }

  /* The 🚩 and 💬 counters: requests waiting for an answer, messages not yet read. */
  .waiting {
    display: inline-block;
    min-width: 1.1rem;
    margin-left: 0.2rem;
    padding: 0 0.3rem;
    border-radius: 999px;
    background: #ff4d4f;
    color: #fff;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1.1rem;
    text-align: center;
  }
</style>
